/**
 * Forging Module - Pure functions for scroll application
 * MapleStory-style scrolling system with permanent slot destruction on failure
 */

import {
  FORGING_CONFIG,
  SCROLL_TYPES,
  GEMSTONES,
  GEMSTONE_IDS,
  SCROLL_STAT_TYPES,
  SCROLL_TIERS,
  getScrollId,
  getScrollByStatAndTier,
  formatScrollStat,
  getStatLabel,
} from '../../data/forging';

/**
 * Get max gem slots for an equipment slot type
 * @param {string} slotType - Equipment slot (weapon, top, helmet, etc.)
 * @returns {number} Maximum gem slots
 */
export function getMaxGemSlots(slotType) {
  return FORGING_CONFIG.maxGemSlots[slotType] || 7;
}

/**
 * Get remaining usable slots for an item
 * @param {Object} item - Equipment item
 * @returns {number} Number of remaining slots
 */
export function getRemainingSlots(item) {
  if (!item || !item.slot) return 0;
  const max = item.maxGemSlots ?? getMaxGemSlots(item.slot);
  const used = item.appliedScrolls?.length || 0;
  const failed = item.failedSlots || 0;
  return Math.max(0, max - used - failed);
}

/**
 * Check if a scroll can be applied to an item
 * @param {Object} item - Equipment item
 * @param {string} scrollId - Scroll ID (e.g., 'attack_2')
 * @param {Object} gemstones - Player's gemstone counts { ruby: 10, sapphire: 5, ... }
 * @returns {{ canApply: boolean, error?: string, gemCost?: number, successRate?: number }}
 */
export function canApplyScroll(item, scrollId, gemstones) {
  const scroll = SCROLL_TYPES[scrollId];
  if (!scroll) {
    return { canApply: false, error: 'Invalid scroll type' };
  }

  if (!item || !item.slot) {
    return { canApply: false, error: 'Invalid equipment' };
  }

  const remaining = getRemainingSlots(item);
  if (remaining <= 0) {
    return { canApply: false, error: 'No available gem slots' };
  }

  const gemCost = FORGING_CONFIG.gemstoneCosts[scroll.tier];
  const playerGems = gemstones?.[scroll.gemstone] || 0;

  if (playerGems < gemCost) {
    const gemstone = GEMSTONES[scroll.gemstone];
    return {
      canApply: false,
      error: `Not enough ${gemstone.name} (need ${gemCost}, have ${playerGems})`,
    };
  }

  const successRate = FORGING_CONFIG.successRates[scroll.tier];

  return {
    canApply: true,
    gemCost,
    successRate,
  };
}

/**
 * Attempt to apply a scroll to equipment
 * @param {Object} item - Equipment item
 * @param {string} scrollId - Scroll ID
 * @returns {{ success: boolean, result: string, newItem: Object, statGained?: Object, slotsDestroyed?: number, roll: number, successRate: number }}
 */
export function attemptScrollApplication(item, scrollId) {
  const scroll = SCROLL_TYPES[scrollId];
  if (!scroll) {
    return {
      success: false,
      result: 'error',
      newItem: item,
      error: 'Invalid scroll',
      roll: 0,
      successRate: 0,
    };
  }

  const tier = scroll.tier;
  const successRate = FORGING_CONFIG.successRates[tier];
  const statBonus = FORGING_CONFIG.statBonuses[tier][scroll.stat];

  const roll = Math.random();
  const isSuccess = roll < successRate;

  // Clone the item
  const newItem = {
    ...item,
    appliedScrolls: [...(item.appliedScrolls || [])],
    failedSlots: item.failedSlots || 0,
  };

  if (isSuccess) {
    // Success - add scroll bonus
    const appliedScroll = {
      scrollId,
      stat: scroll.stat,
      value: statBonus,
      tier: scroll.tier,
    };

    newItem.appliedScrolls.push(appliedScroll);
    newItem.scrollStats = calculateScrollStats(newItem.appliedScrolls);

    return {
      success: true,
      result: 'success',
      newItem,
      statGained: { [scroll.stat]: statBonus },
      roll,
      successRate,
    };
  } else {
    // Failure - destroy a slot permanently (MapleStory authentic)
    newItem.failedSlots += 1;
    // Keep scrollStats unchanged (existing scrolls remain)
    newItem.scrollStats = calculateScrollStats(newItem.appliedScrolls);

    return {
      success: false,
      result: 'fail_destroy_slot',
      newItem,
      slotsDestroyed: 1,
      roll,
      successRate,
    };
  }
}

/**
 * Calculate total scroll stats from applied scrolls
 * @param {Array} appliedScrolls - Array of applied scroll objects
 * @returns {Object} Combined stats { attack: X, defense: Y, ... }
 */
export function calculateScrollStats(appliedScrolls) {
  const stats = {};

  if (!appliedScrolls || appliedScrolls.length === 0) {
    return stats;
  }

  for (const scroll of appliedScrolls) {
    if (scroll.stat && scroll.value) {
      stats[scroll.stat] = (stats[scroll.stat] || 0) + scroll.value;
    }
  }

  return stats;
}

/**
 * Get forging info for display
 * @param {Object} item - Equipment item
 * @param {string} scrollId - Scroll ID (or null for general info)
 * @param {Object} gemstones - Player's gemstone counts
 * @returns {Object|null} Forging info for UI
 */
export function getForgingInfo(item, scrollId, gemstones) {
  if (!item || !item.slot) return null;

  const maxSlots = item.maxGemSlots ?? getMaxGemSlots(item.slot);
  const usedSlots = item.appliedScrolls?.length || 0;
  const failedSlots = item.failedSlots || 0;
  const remainingSlots = Math.max(0, maxSlots - usedSlots - failedSlots);

  // Basic info without scroll selected
  const baseInfo = {
    maxSlots,
    usedSlots,
    failedSlots,
    remainingSlots,
    appliedScrolls: item.appliedScrolls || [],
    scrollStats: item.scrollStats || {},
    hasAvailableSlots: remainingSlots > 0,
  };

  // If no scroll selected, return base info
  if (!scrollId) {
    return baseInfo;
  }

  const scroll = SCROLL_TYPES[scrollId];
  if (!scroll) {
    return { ...baseInfo, scroll: null, canApply: false, error: 'Invalid scroll' };
  }

  const gemCost = FORGING_CONFIG.gemstoneCosts[scroll.tier];
  const gemstone = GEMSTONES[scroll.gemstone];
  const playerGems = gemstones?.[scroll.gemstone] || 0;
  const canAfford = playerGems >= gemCost;

  const successRate = FORGING_CONFIG.successRates[scroll.tier];
  const statBonus = FORGING_CONFIG.statBonuses[scroll.tier][scroll.stat];

  return {
    ...baseInfo,
    scroll,
    gemstone,
    gemCost,
    playerGems,
    canAfford,
    successRate,
    successRatePercent: Math.round(successRate * 100),
    statBonus,
    canApply: remainingSlots > 0 && canAfford,
  };
}

/**
 * Preview what stats would be gained from a scroll
 * @param {Object} item - Equipment item
 * @param {string} scrollId - Scroll ID
 * @returns {{ currentStats: Object, newStats: Object, statChange: Object }|null}
 */
export function previewScrollApplication(item, scrollId) {
  if (!item || !item.slot) return null;

  const scroll = SCROLL_TYPES[scrollId];
  if (!scroll) return null;

  const tier = scroll.tier;
  const statBonus = FORGING_CONFIG.statBonuses[tier][scroll.stat];

  const currentStats = item.scrollStats || {};
  const newStats = {
    ...currentStats,
    [scroll.stat]: (currentStats[scroll.stat] || 0) + statBonus,
  };

  return {
    currentStats,
    newStats,
    statChange: { [scroll.stat]: statBonus },
  };
}

/**
 * Initialize forging properties for a new equipment item
 * @param {Object} item - Equipment item
 * @returns {Object} Item with forging properties
 */
export function initializeForgingProperties(item) {
  if (!item || !item.slot) return item;

  return {
    ...item,
    maxGemSlots: item.maxGemSlots ?? getMaxGemSlots(item.slot),
    appliedScrolls: item.appliedScrolls || [],
    failedSlots: item.failedSlots || 0,
    scrollStats: item.scrollStats || {},
  };
}

/**
 * Clean slate an equipment item - removes all applied scrolls and failed slots
 * Pure function that returns the reset item
 * @param {Object} item - Equipment item to reset
 * @returns {{ success: boolean, newItem: Object, scrollsRemoved: number, slotsRestored: number, error?: string }}
 */
export function cleanSlateEquipment(item) {
  if (!item || !item.slot) {
    return { success: false, error: 'Invalid equipment', newItem: item, scrollsRemoved: 0, slotsRestored: 0 };
  }

  const scrollsRemoved = item.appliedScrolls?.length || 0;
  const slotsRestored = item.failedSlots || 0;

  // Nothing to reset
  if (scrollsRemoved === 0 && slotsRestored === 0) {
    return { success: false, error: 'Item has no forging to reset', newItem: item, scrollsRemoved: 0, slotsRestored: 0 };
  }

  // Create reset item
  const newItem = {
    ...item,
    appliedScrolls: [],
    failedSlots: 0,
    scrollStats: {},
  };

  return {
    success: true,
    newItem,
    scrollsRemoved,
    slotsRestored,
  };
}

/**
 * Check if an item can be clean slated
 * @param {Object} item - Equipment item
 * @returns {boolean}
 */
export function canCleanSlate(item) {
  if (!item || !item.slot) return false;
  const hasScrolls = (item.appliedScrolls?.length || 0) > 0;
  const hasFailedSlots = (item.failedSlots || 0) > 0;
  return hasScrolls || hasFailedSlots;
}

// Re-export data for convenience
export {
  FORGING_CONFIG,
  SCROLL_TYPES,
  GEMSTONES,
  GEMSTONE_IDS,
  SCROLL_STAT_TYPES,
  SCROLL_TIERS,
  getScrollId,
  getScrollByStatAndTier,
  formatScrollStat,
  getStatLabel,
};

export default {
  getMaxGemSlots,
  getRemainingSlots,
  canApplyScroll,
  attemptScrollApplication,
  calculateScrollStats,
  getForgingInfo,
  previewScrollApplication,
  initializeForgingProperties,
  cleanSlateEquipment,
  canCleanSlate,
  FORGING_CONFIG,
  SCROLL_TYPES,
  GEMSTONES,
  GEMSTONE_IDS,
  SCROLL_STAT_TYPES,
  SCROLL_TIERS,
};
